import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TagsLanguageService } from '../../features/tags-language/tagsLanguage.service';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { Storage } from '@angular/fire/storage';
import { ChallengesService } from '../../features/challenges/challenges.service';
import { FullscreenLoaderComponent } from '../../shared/components/loader-component/loader.component';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { MatDialog } from '@angular/material/dialog';
import {
  noOnlySpacesValidator,
  noOnlySpecialCharsAlphaValidator,
} from '../../core/helpers/validator';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';
import { FilenameGeneratorUtil } from '../../core/utils/filename-generator';

@Component({
  selector: 'app-challenges-new',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    FullscreenLoaderComponent,
    ContentLoaderComponent,
  ],
  templateUrl: './challenges-new.component.html',
  styleUrl: './challenges-new.component.scss',
})
export class ChallengesNewComponent {
  @ViewChild('imagefileInput') imagefileInput!: ElementRef<HTMLInputElement>;
  readonly storage = inject(Storage);
  readonly dialog = inject(MatDialog);
  public languages: any[] = [];
  public mainForm: FormGroup;
  public challengeTranslations: {
    id?: number;
    langId: number;
    challengeName: string;
  }[] = [];
  imageUrl: string | null = null;
  imageUploading = false;
  imageFileName: string | null = null;
  challangeId: any;
  loading = false;
  isCustomChallenge = false;
  isEditing: boolean = false;
  isSubmitting = false;
  currTranslationId: any;
  contentLoading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private tagsService: TagsLanguageService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private challengeService: ChallengesService
  ) {
    this.mainForm = this.fb.group({
      imageURL: [''],
      challengeName: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          noOnlySpecialCharsAlphaValidator(),
          noOnlySpacesValidator(),
        ],
      ],
      langId: [1, Validators.required],
    });
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.challangeId = params.get('id');
      if (this.challangeId) {
        this.contentLoading = true;
        this.mainForm.get('langId')?.patchValue(null);
        this.getChallenegById(this.challangeId);
      }
    });
    this.getLanguages();
  }

  getLanguages(): void {
    this.tagsService.getLangauges().subscribe({
      next: (res) => {
        if (res?.data) {
          this.languages = res?.data;
          if (this.challengeTranslations.length === this.languages.length) {
            this.mainForm.get('challengeName')?.disable();
            this.mainForm.get('langId')?.disable();
          }
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  isLangUsed(langId: number): boolean {
    return this.challengeTranslations?.some((t) => t.langId === langId);
  }

  getChallenegById(id: any) {
    this.challengeService.getChallengeById(id).subscribe({
      next: (res) => {
        const challengeData = res?.challenge;
        if (challengeData) {
          this.imageUrl = challengeData.imageURL;

          this.imageFileName = challengeData.imageURL
            ? FilenameGeneratorUtil.generateRandomFilename(challengeData.imageURL)
            : null;

          this.isCustomChallenge = challengeData.isCustom;

          challengeData.translations.forEach((translation: any) => {
            this.challengeTranslations.push({
              id: translation.id,
              langId: translation.langId,
              challengeName: translation.challengeName,
            });
          });
        }
        this.getLanguages();
        this.contentLoading = false;
      },
      error: (err) => {
        this.contentLoading = false;
      },
    });
  }

  onCancel() {
    this.router.navigate(['/challenges']);
  }

  onlyAllowNumbers(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Delete',
    ];
    if (
      (event.key >= '0' && event.key <= '9') ||
      allowedKeys.includes(event.key)
    ) {
      return;
    }
    event.preventDefault();
  }
  blockNonNumericPaste(event: ClipboardEvent): void {
    const pastedInput = event.clipboardData?.getData('text') || '';
    if (!/^\d+$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  getLanguageName(langId: number): string {
    const lang = this.languages.find((l) => l.id === langId);
    return lang ? lang.name : `Lang ID: ${langId}`;
  }

  get sortedChallengeTranslations() {
    return this.challengeTranslations
      ?.slice()
      .sort((a, b) => (a.langId === 1 ? -1 : b.langId === 1 ? 1 : 0));
  }

  addChallengeTable(): void {
    this.isEditing = false;
    const { challengeName, langId } = this.mainForm.value;

    if (this.mainForm.invalid) {
      this.toastr.warning('Please enter all the fields');
      return;
    }

    if (challengeName === '' || challengeName === null) {
      this.toastr.warning('Please enter valid challenge name.');
      return;
    }

    if (langId === '' || langId === null) {
      this.toastr.warning('Please choose a valid language');
      return;
    }

    const isDuplicate = this.challengeTranslations.some(
      (t) => t.langId === langId
    );

    if (isDuplicate) {
      this.toastr.error('This language has already been added.');
      return;
    }

    this.challengeTranslations.push({
      id: this.currTranslationId ?? undefined,
      challengeName,
      langId,
    });

    if (this.challengeTranslations.length === this.languages.length) {
      this.mainForm.get('challengeName')?.disable();
      this.mainForm.get('langId')?.disable();
    }

    this.currTranslationId = null;

    this.mainForm.controls?.['challengeName'].reset();
    this.mainForm.controls?.['challengeName'].setErrors(null);
    this.mainForm.controls?.['challengeName'].markAsUntouched();

    this.mainForm.controls?.['langId'].reset();
    this.mainForm.controls?.['langId'].setErrors(null);
    this.mainForm.controls?.['langId'].markAsUntouched();
  }

  removeChallengeTranslation(index: number, id: any): void {
    const dialogRef = this.dialog.open(ConfirmDialog);
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        id
          ? this.challengeService.deleteChallengeTranslation(id).subscribe({
            next: (res) => {
              this.challengeTranslations.splice(index, 1);
              this.mainForm.get('challengeName')?.enable();
              this.mainForm.controls?.['challengeName'].setErrors(null);
              this.mainForm.controls?.['challengeName'].markAsUntouched();
              this.mainForm.get('langId')?.enable();
              this.mainForm.controls?.['langId'].setErrors(null);
              this.mainForm.controls?.['langId'].markAsUntouched();
            },
            error: (err) => {
              console.log(err);
            },
          })
          : this.challengeTranslations.splice(index, 1);
        this.mainForm.get('challengeName')?.enable();
        this.mainForm.controls?.['challengeName'].setErrors(null);
        this.mainForm.controls?.['challengeName'].markAsUntouched();
        this.mainForm.get('langId')?.enable();
        this.mainForm.controls?.['langId'].setErrors(null);
        this.mainForm.controls?.['langId'].markAsUntouched();
      }
    });
  }

  editChallengeTranslation(index: number): void {
    this.currTranslationId = this.challengeTranslations[index].id;
    if (this.isEditing) {
      this.addChallengeTable();
    }
    this.isEditing = true;
    this.mainForm.get('challengeName')?.enable();
    this.mainForm.get('langId')?.enable();
    const selected = this.challengeTranslations[index];

    this.challengeTranslations.splice(index, 1);
    this.mainForm.patchValue({
      challengeName: selected.challengeName,
      langId: selected.langId,
    });
    this.mainForm.get('challengeName')?.markAsTouched();
    this.mainForm.get('langId')?.markAsTouched();
    this.scrollUp(`translation`);
  }

  scrollUp(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  async onImageUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 15 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Choose proper Image format');
      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      this.toastr.error('Choose a smaller image file (less than 15 MB)');
      input.value = '';
      return;
    }

    this.imageUploading = true;
    if (!navigator.onLine) {
      this.toastr.warning(
        'No internet connection. Please check your connection and try again.'
      );
      input.value = '';
      this.imageUploading = false;
      return;
    }

    const filePath = `story-thumb/${file.name}_${Date.now()}`;
    const fileRef = ref(this.storage, filePath);

    try {
      await uploadBytes(fileRef, file);
      if (!navigator.onLine) {
        input.value = '';
        this.toastr.warning('Upload failed. No internet connection');
        this.imageUploading = false;
        return;
      }
      this.imageUrl = await getDownloadURL(fileRef);
      this.imageFileName = file.name;
    } catch (err) {
      this.toastr.error(
        'Upload failed due to an internal error. Please try again later.'
      );
      console.error('Upload failed--', err);
    } finally {
      this.imageUploading = false;
    }
  }

  clearImageUpload(): void {
    this.imageFileName = null;
    this.imageUrl = null;
    if (this.imagefileInput) {
      this.imagefileInput.nativeElement.value = '';
    }
  }

  createChallenge() {
    if (this.isEditing) {
      this.addChallengeTable();
    }
    if (!this.imageUrl) {
      this.toastr.warning('Please upload an image');
      return;
    }
    const englishTranslation = this.challengeTranslations.find(
      (t) => t.langId === 1
    );

    if (!englishTranslation) {
      this.toastr.error(
        'Challenge in English is mandatory, Please add it to continue'
      );
      return;
    }

    const payload = {
      isCustom: this.isCustomChallenge,
      imageURL: this.imageUrl,
      translations: this.challengeTranslations,
    };

    this.loading = true;
    this.isSubmitting = true;
    if (this.challangeId) {
      this.challengeService
        .updateChallenge(this.challangeId, payload)
        .subscribe({
          next: (res) => {
            this.toastr.success('Challenge updated successfully.');
            this.mainForm.reset();
            this.isSubmitting = false;
            this.router.navigate(['/challenges']);
          },
          error: (err) => {
            this.isSubmitting = false;
            this.loading = false;
          },
          complete: () => {
            this.isSubmitting = false;
            this.loading = false;
          },
        });
    } else {
      this.challengeService.createChallenge(payload).subscribe({
        next: (res) => {
          this.toastr.success('Challenge created successfully.');
          this.mainForm.reset();
          this.isSubmitting = false;
          this.router.navigate(['/challenges']);
        },
        error: (err) => {
          this.isSubmitting = false;
          this.loading = false;
        },
        complete: () => {
          this.isSubmitting = false;
          this.loading = false;
        },
      });
    }
  }
}
